/**
 * PsDeckMaps — PropertyScout integration shim for Deck.gl pattern overlays.
 *
 * Exposes a global `window.PsDeckMaps` namespace mirroring the pattern used by
 * `window.PsGrids` and `window.PsEmbeddedMaps`. Auto-installs the deck.gl
 * issue #7326 shader workaround on first patterned layer creation.
 *
 * Reach for this when you want to drop a hatch/dot/stripe/icon-style pattern
 * fill onto polygons in a Mapbox map, especially at parcel zoom. Reach for
 * Mapbox native `fill-pattern` only for very simple cases where its zoom
 * stability and recoloring limitations are acceptable.
 *
 * See agent wiki tech/mapbox.md for full background and the why-this-exists
 * story, plus PropertyScout issue #268.
 *
 * --- Quick example ---
 *
 *   const overlay = new deck.MapboxOverlay({interleaved: true, layers: []});
 *   map.addControl(overlay);
 *
 *   // After overlay is added, its device exists.
 *   requestAnimationFrame(() => {
 *     const device = overlay._deck.device;
 *     overlay.setProps({
 *       layers: [
 *         window.PsDeckMaps.createPatternedLayer({
 *           device,
 *           id: 'selected-lot-pattern',
 *           data: {type: 'FeatureCollection', features: [selectedLotFeature]},
 *           pattern: 'dots',
 *           color: [0, 0, 0, 128],          // black @ 50% alpha
 *           lineColor: [0, 0, 0, 0],         // no extra stroke (Mapbox draws the lot border)
 *           tileWorldMeters: 12,             // smaller tile = denser dots
 *           beforeId: 'lot-labels copy'      // keep Mapbox labels readable on top
 *         })
 *       ]
 *     });
 *   });
 */

// Named imports so Vite tree-shaking keeps these classes in the bundle.
// (A namespace import like `import * as core from '@deck.gl/core'` gets
// tree-shaken aggressively when nothing statically references the names.)
import {
  Deck,
  Layer,
  CompositeLayer,
  LayerExtension,
  COORDINATE_SYSTEM,
  MapView,
  OrbitView,
  OrthographicView,
  FirstPersonView,
  MapController,
  WebMercatorViewport,
  AmbientLight,
  DirectionalLight,
  PointLight,
  LightingEffect,
  log
} from '@deck.gl/core';
import {
  ArcLayer,
  BitmapLayer,
  ColumnLayer,
  GeoJsonLayer,
  GridCellLayer,
  IconLayer,
  LineLayer,
  PathLayer,
  PointCloudLayer,
  PolygonLayer,
  ScatterplotLayer,
  SolidPolygonLayer,
  TextLayer
} from '@deck.gl/layers';
import {
  BrushingExtension,
  ClipExtension,
  CollisionFilterExtension,
  DataFilterExtension,
  FillStyleExtension,
  Fp64Extension,
  MaskExtension,
  PathStyleExtension
} from '@deck.gl/extensions';
import {MapboxOverlay} from '@deck.gl/mapbox';

import {createPatternedLayer, makeDotsCanvas, makeDiagonalStripeCanvas, makeCrosshatchCanvas, makeVerticalStripesCanvas, makeMippedTexture, PATTERN_PRESETS} from './pattern_layer.js';
import {installNoModInject, verifyPatchOnLayer, isInstalled} from './fill_style_patch.js';

// Assemble the `deck` namespace from named imports. Lets callers access
// raw Deck classes via PsDeckMaps.deck.{...} or window.deck.{...} the same
// way the CDN-style global does.
const deckBundle = {
  // core
  Deck, Layer, CompositeLayer, LayerExtension, COORDINATE_SYSTEM,
  MapView, OrbitView, OrthographicView, FirstPersonView,
  MapController, WebMercatorViewport,
  AmbientLight, DirectionalLight, PointLight, LightingEffect,
  log,
  // layers
  ArcLayer, BitmapLayer, ColumnLayer, GeoJsonLayer, GridCellLayer,
  IconLayer, LineLayer, PathLayer, PointCloudLayer, PolygonLayer,
  ScatterplotLayer, SolidPolygonLayer, TextLayer,
  // extensions
  BrushingExtension, ClipExtension, CollisionFilterExtension,
  DataFilterExtension, FillStyleExtension, Fp64Extension,
  MaskExtension, PathStyleExtension,
  // mapbox
  MapboxOverlay
};

const PsDeckMaps = {
  // High-level factory (recommended entry point).
  createPatternedLayer,

  // Low-level pattern canvas/texture helpers (for custom pattern needs).
  makeDotsCanvas,
  makeDiagonalStripeCanvas,
  makeCrosshatchCanvas,
  makeVerticalStripesCanvas,
  makeMippedTexture,
  PATTERN_PRESETS,

  // Shader-patch installation + diagnostics.
  installNoModInject,
  verifyPatchOnLayer,
  isInstalled,

  // Raw deck.gl bundle for callers that want MapboxOverlay, Deck, etc.
  deck: deckBundle
};

if (typeof window !== 'undefined') {
  window.PsDeckMaps = PsDeckMaps;
  // Expose `window.deck` for code that uses the CDN-style global.
  // @deck.gl/core registers its own tiny window.deck on import
  // (just {VERSION, log, version, _registerLoggers}); merge our bundle on top
  // so the global has the full class set without losing what core registered.
  window.deck = Object.assign({}, window.deck || {}, deckBundle);
}

export default PsDeckMaps;
export {createPatternedLayer, installNoModInject, verifyPatchOnLayer, isInstalled};
